import * as React from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import AppTheme from "../components/shared-theme/AppTheme";
import { SynergiIcon } from "../components/sign-in/components/CustomIcons";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { verifyEmail } from "../services/authService";
import { useEffect, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: { maxWidth: "450px" },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
}));

const VerifyEmailContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: { padding: theme.spacing(4) },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
  },
}));

type Status = "idle" | "loading" | "success" | "error";

export default function VerifyEmail(props: { disableCustomTheme?: boolean }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Missing verification link.");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    verifyEmail(token)
      .then(() => {
        if (!cancelled) setStatus("success");
      })
      .catch((err: any) => {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(
            err?.response?.data?.error || "Verification failed. The link may have expired."
          );
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <VerifyEmailContainer direction="column" justifyContent="center">
        <Card variant="outlined">
          <SynergiIcon />
          <Typography component="h1" variant="h5" sx={{ fontSize: "clamp(1.25rem, 5vw, 1.5rem)" }}>
            Verify your email
          </Typography>

          {status === "loading" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Verifying your email address…</Typography>
            </Box>
          )}

          {status === "success" && (
            <>
              <Alert severity="success" sx={{ width: "100%" }}>
                Your email address has been verified. You can now sign in with your new email.
              </Alert>
              <Button component={RouterLink} to="/sign-in" variant="contained" fullWidth>
                Sign in
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <Alert severity="error" sx={{ width: "100%" }}>
                {errorMessage}
              </Alert>
              <Button component={RouterLink} to="/sign-in" variant="outlined" fullWidth>
                Go to sign in
              </Button>
            </>
          )}

          {status === "idle" && token && (
            <Box sx={{ py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Card>
      </VerifyEmailContainer>
    </AppTheme>
  );
}
